import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SocialIcon } from "react-social-icons";
import {
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { fetchData } from "./utils/statamic-api";
import Loading from "./Loading";

const Footer = ({ logo }) => {
  const [companyInfo, setCompanyInfo] = useState(null);
  const [socialLinksData, setSocialLinksData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const companyData = await fetchData("/globals/company_details?site=de");
        setCompanyInfo(companyData.data);
        const socialData = await fetchData("/globals/social?site=de");
        setSocialLinksData(socialData.data);
      } catch (error) {
        console.error("Error fetching footer data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFooterData();
  }, []);

  if (loading) return <Loading />;

  if (!companyInfo) return <p>Company information is unavailable.</p>;

  // Build social link URLs (only those that exist)
  const socialUrls = [
    socialLinksData.facebook &&
      `https://www.facebook.com/${socialLinksData.facebook}`,
    socialLinksData.twitter && `https://twitter.com/${socialLinksData.twitter}`,
    socialLinksData.linkedin &&
      `https://www.linkedin.com/company/${socialLinksData.linkedin}`,
    socialLinksData.instagram &&
      `https://www.instagram.com/${socialLinksData.instagram}`,
    socialLinksData.youtube &&
      `https://www.youtube.com/user/${socialLinksData.youtube}`,
    socialLinksData.vimeo && `https://vimeo.com/${socialLinksData.vimeo}`,
  ].filter(Boolean);

  const companyName = companyInfo.name || "Frontier Medical Group";
  const year = new Date().getFullYear();

  const ContactRow = ({ icon: Icon, children }) => (
    <li className="flex items-center gap-4">
      <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-white/10">
        <Icon className="h-5 w-5 text-white" aria-hidden="true" />
      </span>
      <span className="whitespace-pre-line text-white/90">{children}</span>
    </li>
  );

  return (
    <footer
      className="mx-auto max-w-7xl px-6 pb-10 lg:px-8"
      aria-labelledby="footer-heading"
    >
      <h2 id="footer-heading" className="sr-only">
        Footer
      </h2>

      <div className="rounded-3xl bg-fmg-blue-600 px-8 py-12 text-white sm:px-12 lg:px-16 lg:py-16">
        {/* Main content */}
        <div className="flex flex-col gap-12 lg:flex-row lg:justify-between">
          {/* Brand */}
          <div className="max-w-sm">
            <img
              className="h-12 w-auto brightness-0 invert"
              src={logo}
              alt={companyName}
            />

            {companyInfo.short_description && (
              <p className="mt-6 text-sm leading-7 text-white/70">
                {companyInfo.short_description}
              </p>
            )}

            {socialUrls.length > 0 && (
              <div className="mt-8 flex gap-2">
                {socialUrls.map((url, index) => (
                  <SocialIcon
                    key={index}
                    url={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    bgColor="transparent"
                    fgColor="#ffffff"
                    style={{ width: 40, height: 40 }}
                    className="opacity-80 transition-opacity hover:opacity-100"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Contact */}
          <div className="lg:min-w-[18rem]">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-white/60">
              Kontakt
            </h3>
            <ul className="mt-6 space-y-5 text-sm">
              {companyInfo.address && (
                <ContactRow icon={MapPinIcon}>{companyInfo.address}</ContactRow>
              )}
              {companyInfo.phone_number && (
                <ContactRow icon={PhoneIcon}>
                  <a
                    className="hover:text-white"
                    href={`tel:${companyInfo.phone_number.replace(/\s+/g, "")}`}
                  >
                    {companyInfo.phone_number}
                  </a>
                </ContactRow>
              )}
              {companyInfo.contact_email && (
                <ContactRow icon={EnvelopeIcon}>
                  <a
                    className="hover:text-white"
                    href={`mailto:${companyInfo.contact_email}`}
                  >
                    {companyInfo.contact_email}
                  </a>
                </ContactRow>
              )}
              {companyInfo.opening_hours && (
                <ContactRow icon={ClockIcon}>
                  {companyInfo.opening_hours}
                </ContactRow>
              )}
            </ul>
          </div>
        </div>

        {/* Fine print: legal disclosure (§5 TMG) + copyright */}
        <div className="mt-12 flex flex-col gap-3 border-t border-white/15 pt-8 text-xs leading-5 text-white/55 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {year} {companyName} · Frontier Medical Germany GmbH ·
            Geschäftsführer: Mark Phillips · Amtsgericht Köln HRB 107019 ·
            USt-IdNr. DE355566859
          </p>
          <Link
            to="/datenschutz"
            className="flex-none hover:text-white"
          >
            Datenschutzerklärung
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
